import { DateUtil, isNullOrUndefined } from '@uzurv/core';
import { autoserialize, autoserializeAs, inheritSerialization } from 'cerialize';
import moment from 'moment-timezone';
import { RideStatusConstants } from '../constants';
import { CancellationReason } from './cancellation-reason';
import { ChatMessage } from './chat-message';
import { ClientFeeScheduleDetail } from './client-fee-schedule-detail';
import { Driver } from './driver';
import { DriverEarnings } from './driver-earnings';
import { MiniRider } from './mini-rider';
import { OrganizationPaymentMethod } from './organization-payment-method';
import { PaymentCollectionType } from './payment-collection-type';
import { ProviderFeeScheduleDetail } from './provider-fee-schedule-detail';
import { ReservationStatus } from './reservation-status';
import { RideInfo } from './ride-info';
import { TransitProgramInfo } from './transit-program-info';
import { Vehicle } from './vehicle';
import { VehicleType } from './vehicle-type';

@inheritSerialization(RideInfo)
export class RideDetail extends RideInfo {
  @autoserializeAs(Date)
  itineraryPickupDate: Date;

  @autoserializeAs(VehicleType)
  vehicleType: VehicleType;

  @autoserialize
  activated: boolean;

  @autoserializeAs(Date)
  activatedAt: Date;

  @autoserialize
  polyline: string;

  @autoserialize
  distanceEstimate: number;

  @autoserialize
  tollEstimate: number;

  @autoserialize
  driverFareEstimate: number;

  @autoserialize
  passengerCount: number;

  @autoserialize
  billablePassengerCount: number;

  @autoserialize
  accepted: boolean;

  @autoserializeAs(Date)
  acceptedDate: Date;

  @autoserialize
  cancelled: boolean;

  @autoserializeAs(CancellationReason)
  cancellationReason: CancellationReason;

  @autoserializeAs(Date)
  cancelledDate: Date;

  @autoserialize
  active: boolean;

  @autoserialize
  requestFee: number;

  @autoserializeAs(PaymentCollectionType)
  riderPaymentCollectionType: PaymentCollectionType;

  @autoserialize
  incentive: number;

  @autoserializeAs(MiniRider)
  miniRider: MiniRider;

  @autoserializeAs(TransitProgramInfo)
  transitProgramInfo: TransitProgramInfo;

  @autoserialize
  riderNote: string;

  @autoserializeAs(Driver)
  driver: Driver;

  @autoserializeAs(Vehicle)
  vehicle: Vehicle;

  @autoserializeAs(Date)
  driverEnRouteDate: Date;

  @autoserializeAs(Date)
  pickUpArrivedDate: Date;

  @autoserializeAs(Date)
  inProgressDate: Date;

  @autoserializeAs(Date)
  dropOffArrivedDate: Date;

  @autoserializeAs(Date)
  completedDate: Date;

  @autoserialize
  riderSignatureCaptureEnabled: boolean;

  @autoserialize
  driverIdBadgeRequired: boolean;

  @autoserializeAs(ChatMessage)
  chatMessages: Array<ChatMessage>;

  @autoserializeAs(OrganizationPaymentMethod)
  supportedPaymentMethods: Array<OrganizationPaymentMethod>;

  @autoserialize
  journeyId: number;

  @autoserializeAs(ClientFeeScheduleDetail)
  clientFeeScheduleDetail: ClientFeeScheduleDetail;

  @autoserializeAs(ProviderFeeScheduleDetail)
  providerFeeScheduleDetail: ProviderFeeScheduleDetail;

  @autoserializeAs(DriverEarnings)
  driverEarnings: DriverEarnings;

  get driverFareEstimatePlusIncentive(): number {
    if (!isNullOrUndefined(this.incentive)) {
      return this.driverFareEstimate + this.incentive;
    }
    return this.driverFareEstimate;
  }

  get displayDate(): Date {
    if (!isNullOrUndefined(this.pickupDate)) {
      return this.pickupDate;
    } else if (!isNullOrUndefined(this.inProgressDate)) {
      return this.inProgressDate;
    } else if (this.cancelled) {
      return this.cancelledDate;
    }

    return this.itineraryPickupDate;
  }

  get status(): string {
    // It's a ride request
    if (isNullOrUndefined(this.reservationId)) {
      if (!this.cancelled) {
        return RideStatusConstants.CANCELLED;
      }

      return RideStatusConstants.PENDING;
    }

    // Otherwise just use the status
    return this.reservationStatus.name;
  }

  /**
   * TODO: Refactor later using https://www.npmjs.com/package/localdate
   */
  get formattedDisplayDate(): string {
    if (!this.rideRequestType.onDemand) {
      return moment.tz(this.pickupDate, this.timeZone).format('M/DD/YY h:mm a (z)');
    }
    return moment(this.itineraryPickupDate).format('M/DD/YY');
  }

  static OnSerialized(instance: RideDetail, json: any): void {
    super.OnSeserialized(instance, json);

    if (!isNullOrUndefined(instance.itineraryPickupDate)) {
      json.itineraryPickupDate = DateUtil.serializeLocalDate(instance.itineraryPickupDate);
    }

    if (!isNullOrUndefined(instance.activatedAt)) {
      json.activatedAt = DateUtil.serializeDate(instance.activatedAt);
    }

    if (!isNullOrUndefined(instance.acceptedDate)) {
      json.acceptedDate = DateUtil.serializeDate(instance.acceptedDate);
    }

    if (!isNullOrUndefined(instance.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(instance.cancelledDate);
    }

    if (!isNullOrUndefined(instance.riderNote)) {
      const riderNote: string = instance.riderNote.trim();
      json.riderNote = riderNote.length === 0 ? null : riderNote;
    }

    if (!isNullOrUndefined(instance.completedDate)) {
      json.completedDate = DateUtil.serializeDate(instance.completedDate);
    }

    if (!isNullOrUndefined(instance.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(instance.cancelledDate);
    }

    if (!isNullOrUndefined(instance.pickUpArrivedDate)) {
      json.pickUpArrivedDate = DateUtil.serializeDate(instance.pickUpArrivedDate);
    }

    if (!isNullOrUndefined(instance.inProgressDate)) {
      json.inProgressDate = DateUtil.serializeDate(instance.inProgressDate);
    }
  }

  static OnDeserialized(instance: RideDetail, json: any): void {
    super.OnDeserialized(instance, json);

    if (!isNullOrUndefined(json['itineraryPickupDate'])) {
      instance.itineraryPickupDate = DateUtil.deserializeLocalDate(json['itineraryPickupDate']);
    }

    if (!isNullOrUndefined(json['activatedAt'])) {
      instance.activatedAt = DateUtil.deserializeDate(json['activatedAt']);
    }

    if (!isNullOrUndefined(json['acceptedDate'])) {
      instance.acceptedDate = DateUtil.deserializeDate(json['acceptedDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['driverEnRouteDate'])) {
      instance.driverEnRouteDate = DateUtil.deserializeDate(json['driverEnRouteDate']);
    }

    if (!isNullOrUndefined(json['pickUpArrivedDate'])) {
      instance.pickUpArrivedDate = DateUtil.deserializeDate(json['pickUpArrivedDate']);
    }

    if (!isNullOrUndefined(json['inProgressDate'])) {
      instance.inProgressDate = DateUtil.deserializeDate(json['inProgressDate']);
    }

    if (!isNullOrUndefined(json['dropOffArrivedDate'])) {
      instance.dropOffArrivedDate = DateUtil.deserializeDate(json['dropOffArrivedDate']);
    }

    if (!isNullOrUndefined(json['completedDate'])) {
      instance.completedDate = DateUtil.deserializeDate(json['completedDate']);
    }
  }
}
